import { Typography } from '@/components/atoms';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, View } from 'react-native';
import { useTranslation } from '@/core/hooks';


export interface LanguageSwitchProps {
    value: 'en' | 'ar';
    onChange: (value: 'en' | 'ar') => void;
}

export function LanguageSwitch({ value, onChange }: LanguageSwitchProps) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const CONTROL_WIDTH = 160; // fixed width to prevent layout growth loops
    const PADDING = 4; // outer padding
    const INNER_WIDTH = CONTROL_WIDTH - PADDING * 2; // available width after padding
    const SEGMENT_WIDTH = INNER_WIDTH / 2; // each segment is exactly half (76px)

    // Visual order of the two segments — mirrored on native RTL (Arabic first/left),
    // fixed English-first on web. The row itself never flex-reverses; only which
    // language renders in which slot changes, so the pill's own math stays simple.
    const mirrorOrder = Platform.OS !== 'web' && isRTL;
    const firstLang: 'en' | 'ar' = mirrorOrder ? 'ar' : 'en';
    const secondLang: 'en' | 'ar' = mirrorOrder ? 'en' : 'ar';

    // Initialize progress: 0 = first slot, 1 = second slot
    const progress = useRef(new Animated.Value(value === firstLang ? 0 : 1)).current;
    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    // Animate when value (or the slot order) changes
    useEffect(() => {
        const targetValue = value === firstLang ? 0 : 1;

        // Cancel any ongoing animation to prevent lag
        if (animationRef.current) {
            animationRef.current.stop();
        }

        // Start new animation
        animationRef.current = Animated.timing(progress, {
            toValue: targetValue,
            duration: 150,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: Platform.OS !== 'web', // Use native driver on native, false on web to animate left reliably
        });

        animationRef.current.start(() => {
            animationRef.current = null;
        });

        // Cleanup on unmount
        return () => {
            if (animationRef.current) {
                animationRef.current.stop();
                animationRef.current = null;
            }
        };
    }, [value, progress, firstLang]);

    // Position 0 = first slot (left), Position SEGMENT_WIDTH = second slot (right).
    // Which language occupies which slot is already handled by firstLang/secondLang,
    // so this mapping never needs an isRTL branch of its own.
    const animLeft = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [PADDING, PADDING + SEGMENT_WIDTH],
    });

    const translateX = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, SEGMENT_WIDTH],
    });

    const renderLabel = (lang: 'en' | 'ar') => (
        <Typography
            variant="subhead"
            style={{
                color: value === lang ? colors.white : colors.fontPrimary,
                ...(lang === 'ar' && Platform.OS !== 'web'
                    ? {
                          // Ensure Arabic glyphs render on native (custom font may not include Arabic)
                          fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
                      }
                    : null),
            }}
        >
            {lang === 'en' ? 'English' : 'العربية'}
        </Typography>
    );

    return (
        <View
            style={{
                position: 'relative',
                // Immune to native RTL auto-mirroring — the flex row's child
                // order AND the pill's absolute `left` position were getting
                // mirrored independently/inconsistently, so which segment the
                // pill visually covered didn't match which segment the JS
                // color logic (based on firstLang/secondLang) thought was
                // selected — hence black-on-black / white-on-white text.
                // firstLang/secondLang below already resolve the correct
                // physical order deterministically, so no native help needed.
                direction: 'ltr',
                flexDirection: 'row',
                alignItems: 'center',
                padding: PADDING,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: colors.outlineActive,
                overflow: 'hidden',
                backgroundColor: colors.backgroundLevel1,
                width: CONTROL_WIDTH,
                minWidth: CONTROL_WIDTH,
            }}
        >
            {/* Black pill indicator - animates between the first and second slot */}
            <Animated.View
                pointerEvents="none"
                style={
                    [
                        {
                            position: 'absolute',
                            top: PADDING,
                            bottom: PADDING,
                            width: SEGMENT_WIDTH,
                            borderRadius: 16,
                            backgroundColor: colors.fontPrimary,
                        },
                        Platform.OS === 'web'
                            ? { left: animLeft }
                            : { left: PADDING, transform: [{ translateX }] },
                    ]
                }
            />
            <Pressable
                accessibilityRole="button"
                onPress={() => onChange(firstLang)}
                style={{
                    width: SEGMENT_WIDTH,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                }}
            >
                {renderLabel(firstLang)}
            </Pressable>
            <Pressable
                accessibilityRole="button"
                onPress={() => onChange(secondLang)}
                style={{
                    width: SEGMENT_WIDTH,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                }}
            >
                {renderLabel(secondLang)}
            </Pressable>
        </View>
    );
}

export default LanguageSwitch;
